import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import { ICodeLoader, IFluidModule, isFluidBrowserPackage } from '@fluidframework/container-definitions';
import {
  IFluidCodeDetails,
  IFluidCodeDetailsComparer,
  IFluidPackage,
  IProvideFluidCodeDetailsComparer
} from '@fluidframework/core-interfaces';
import { CodeVersionDetails } from '@fluidx/office-fluid-types';
import { loadScriptWithRetries, sendTelemetryEvent } from '@fluidx/utilities';
import { getContainerTypeForLogging } from './defaultAllowedCodeOrigins';
import { areAllCodeUrlsAllowed } from './isAllowedCodeUrl';
import { prefetchAssets } from './prefetchAssets';
import { GenericEvent, GenericEventSubCategory } from './telemetry/GenericEvent';
import { ActualCodeDetailsGetter, IExpandedFluidCodeDetails, IVersionedFluidBrowserPackage } from './types';

export interface SimpleCodeLoaderConfig {
  /**
   *  By default, the code loader will try to load from the the fluidCodeDetails.package.fluid.browser.umd field.
   *  Use this override if you wish to use alternate logic to populate the fluidCodeDetails.package field, such as fetching the latest container version via a web request
   */
  getFluidCodeDetailsPackageOverride?: (packageName: string) => Promise<IVersionedFluidBrowserPackage | undefined>;

  /**
   * The list of URL origins we will allow loading code from
   */
  allowedCodeOrigins: string[];

  /**
   * Some containers expect to have code version details injected into them. This callback receives the fluid code details that is actually used to load
   * the container, so it will return the override if one was provided by getFluidCodeDetailsOverride
   */
  getFluidCodeVersionDetails?: (fluidPackage: IVersionedFluidBrowserPackage) => CodeVersionDetails | undefined;
}

function assertExpandedFluidCodeDetails(
  fluidCodeDetails: IFluidCodeDetails
): asserts fluidCodeDetails is IExpandedFluidCodeDetails {
  if (typeof fluidCodeDetails.package === 'string' || assertVersionedFluidBrowserPackage(fluidCodeDetails.package)) {
    throw new Error('Codeloader does not know how to load from this source');
  }
}

function assertVersionedFluidBrowserPackage(
  fluidPackage: IFluidPackage
): asserts fluidPackage is IVersionedFluidBrowserPackage {
  if (!isFluidBrowserPackage(fluidPackage) || fluidPackage.version === undefined) {
    throw new Error('Codeloader does not know how to load from this source');
  }
}

// API that should only be used to reset state between tests
// eslint-disable-next-line @typescript-eslint/naming-convention
export function DO_NOT_USE_clearFluidModuleCache() {
  fluidModuleCache.clear();
}

// The container types we support today only support having one code version loaded per JavaScript context.
// This means that we need to make sure that any request for a given container package name always returns the same code
// There is also the need to make sure that the summarizer gets the exact same version of the code as the interactive client.
const fluidModuleCache = new Map<string, Promise<IFluidModule>>();

const getCacheKeyForPackage = (fluidCodeDetails: IFluidCodeDetails) => {
  if (typeof fluidCodeDetails.package === 'string') {
    return fluidCodeDetails.package;
  }

  return fluidCodeDetails.package.name;
};

const initialPkgVersion = '0.0.1';
type NumericPackageVersion = number | typeof initialPkgVersion;

const isValidVersionNumber = (version: number): boolean => !Number.isNaN(version) && Number.isFinite(version);

const isNumericVersionedPackage = (pkg: IFluidPackage): pkg is Readonly<IVersionedFluidBrowserPackage> => {
  if (!isFluidBrowserPackage(pkg)) {
    return false;
  }
  // This is back-compat case with original initial version.
  if (pkg.version === initialPkgVersion) {
    return true;
  }
  return isValidVersionNumber(Number(pkg.version));
};

const tryGetNameVersionFromCodeDetails = (
  codeDetails: IFluidCodeDetails
): { name: string; version: NumericPackageVersion } | undefined =>
  typeof codeDetails.package !== 'string' && isNumericVersionedPackage(codeDetails.package)
    ? {
        name: codeDetails.package.name,
        version:
          codeDetails.package.version === initialPkgVersion ? initialPkgVersion : Number(codeDetails.package.version)
      }
    : undefined;

const tryGetMaxUnsupportedVersion = (pkg: IFluidPackage): number | undefined => {
  const maxUnsupportedVersion = Number(pkg.maxUnsupportedVersion);
  return isValidVersionNumber(maxUnsupportedVersion) ? maxUnsupportedVersion : undefined;
};

/*
 * A code loader that fetches container code from a CDN based either on the code details in the code proposal or some external means.
 */
export class SimpleCodeLoader implements ICodeLoader, IProvideFluidCodeDetailsComparer, ActualCodeDetailsGetter {
  public get IFluidCodeDetailsComparer(): IFluidCodeDetailsComparer {
    return this;
  }

  // Keys are the concatenation of version numbers and names of original code details package
  private readonly actualCodeDetailsMap = new Map<string, IFluidCodeDetails>();

  /** Returns undefined if it is not valid */
  private tryFormActualCodeDetailsMapKey(codeDetails: IFluidCodeDetails): string | undefined {
    const nameVersion = tryGetNameVersionFromCodeDetails(codeDetails);
    if (nameVersion === undefined) {
      return undefined;
    }
    // Assuming pipe (|) should be invalid number character, then no key collisions
    return `${nameVersion.version}|${nameVersion.name}`;
  }

  public getActualCodeDetails(codeDetails: IFluidCodeDetails): IFluidCodeDetails | undefined {
    const mapKey = this.tryFormActualCodeDetailsMapKey(codeDetails);
    return mapKey === undefined ? undefined : this.actualCodeDetailsMap.get(mapKey);
  }

  // TODO: This path/constructor probably can't be used when we actually use base-host and use the versioned quorum value
  public constructor(private config: SimpleCodeLoaderConfig) {}

  public async load<T extends IFluidModule>(
    fluidCodeDetails: IFluidCodeDetails,
    logger?: ITelemetryBaseLogger
  ): Promise<T> {
    const cacheKey = getCacheKeyForPackage(fluidCodeDetails);

    const cachedModule = fluidModuleCache.get(cacheKey);
    if (cachedModule !== undefined) {
      return cachedModule as Promise<T>;
    }

    const fluidModulePromise = this.loadInternal<T>(fluidCodeDetails, logger);
    fluidModuleCache.set(cacheKey, fluidModulePromise);

    // In the event we fail to load the Fluid module, we delete the promise from the cache so that future attempts to load will retry
    fluidModulePromise.catch(() => fluidModuleCache.delete(cacheKey));

    return fluidModulePromise;
  }

  private async loadInternal<T extends IFluidModule>(
    fluidCodeDetails: IFluidCodeDetails,
    logger?: ITelemetryBaseLogger
  ): Promise<T> {
    const fluidPackageToUse = await this.getEffectiveFluidCodeDetailsPackage(fluidCodeDetails);

    // Add a log that tells what the current type and version is of the experience being loaded
    logger &&
      sendTelemetryEvent(logger, {
        eventName: GenericEvent.FluidCodeDetails,
        subCategory: GenericEventSubCategory.FluidCodeDetails,
        containerType: getContainerTypeForLogging(fluidPackageToUse.name),
        codeVersion: fluidPackageToUse.version
      });

    if (fluidPackageToUse === undefined) {
      throw new Error(
        `Failed to get Fluid Package. Trying to load ContainerType: ${getContainerTypeForLogging(
          (fluidCodeDetails.package as IFluidPackage).name
        )}`
      );
    }

    const containerSource = fluidPackageToUse.fluid.browser.umd;
    if (!this.shouldLoad(containerSource.files)) {
      throw new Error('Unsupported container files, check that CDN is on allow list');
    }

    const mapKey = this.tryFormActualCodeDetailsMapKey(fluidCodeDetails);
    if (mapKey !== undefined && typeof fluidCodeDetails.package !== 'string') {
      const actualCodeDetails = {
        ...fluidCodeDetails,
        package: {
          ...fluidCodeDetails.package,
          ...fluidPackageToUse
        }
      };

      this.actualCodeDetailsMap.set(mapKey, actualCodeDetails);
    }

    await Promise.all(containerSource.files.map(async (file) => await loadScriptWithRetries(file)));

    const containerFactory = (window as any)[containerSource.library];

    if (containerFactory === undefined) {
      throw new Error(
        `Entry point of loaded package not a Fluid module. Trying to load ContainerType: ${getContainerTypeForLogging(
          (fluidCodeDetails.package as IFluidPackage).name
        )}`
      );
    }

    // Some containers expect to have code version details injected into them by the code loader, if that's the case we do that here
    const { CodeVersionDetailsProvider } = containerFactory.fluidExport;
    if (CodeVersionDetailsProvider) {
      const codeVersionDetails =
        this.config.getFluidCodeVersionDetails && this.config.getFluidCodeVersionDetails(fluidPackageToUse);

      if (codeVersionDetails) {
        CodeVersionDetailsProvider.setCodeVersionDetails(codeVersionDetails);
      }
    }

    return containerFactory;
  }

  /**
   * Preloads script files for a given container to have them available in the browser cache for faster loads later
   */
  public async preCache(packageName: string): Promise<void> {
    if (this.config.getFluidCodeDetailsPackageOverride) {
      const packageDetails = await this.config.getFluidCodeDetailsPackageOverride(packageName);

      if (packageDetails) {
        const containerSource = packageDetails.fluid.browser.umd;
        if (!this.shouldLoad(containerSource.files)) {
          throw new Error('Unsupported container files, check that CDN is on allow list');
        }

        prefetchAssets(containerSource.files);
      }
    }
  }

  /**
   * Gets the actual package information that should be used by this code loader after all overrides have been applies
   */
  private async getEffectiveFluidCodeDetailsPackage(
    fluidCodeDetails: IFluidCodeDetails
  ): Promise<IVersionedFluidBrowserPackage> {
    assertExpandedFluidCodeDetails(fluidCodeDetails);

    let packageOverride: IVersionedFluidBrowserPackage | undefined = undefined;
    if (this.config.getFluidCodeDetailsPackageOverride) {
      packageOverride = await this.config.getFluidCodeDetailsPackageOverride(fluidCodeDetails.package.name);
    }

    const fluidPackageToUse = packageOverride ?? fluidCodeDetails.package;

    return fluidPackageToUse;
  }

  private shouldLoad(containerSourceFiles: string[]) {
    return areAllCodeUrlsAllowed(containerSourceFiles, this.config.allowedCodeOrigins);
  }

  public async satisfies(candidate: IFluidCodeDetails, constraint: IFluidCodeDetails): Promise<boolean> {
    // Fetch actual code details, because the candidate version is the previous code details
    // in the quorum rather than the actual loaded version. This gets us stuck until we start
    // passing the actual loaded version.
    const actualCandidate = this.getActualCodeDetails(candidate);
    if (actualCandidate === undefined) {
      // Always return true if actual candidate isn't found to be safe.
      return true;
    }
    if (typeof constraint.package === 'string' || typeof actualCandidate.package === 'string') {
      // We are treating unknown as satisfies for now.
      return true;
    }
    const maxUnsupportedVersion = tryGetMaxUnsupportedVersion(constraint.package);
    if (maxUnsupportedVersion !== undefined) {
      const candidateVersion = tryGetNameVersionFromCodeDetails(actualCandidate)?.version;
      if (candidateVersion !== undefined) {
        // Note that we are only checking one direction. The constraint package will be the one
        // just accepted into the quorum, and the candidate will be the one that is already loaded.
        return candidateVersion === initialPkgVersion || candidateVersion > maxUnsupportedVersion;
      }
    }
    // Should we be correlating the package names as well? If not, we support renames
    // for free, but get no protection against invalid package names.
    // We are considering everything else as satisfactory by default.
    return true;
  }

  public async compare(a: IFluidCodeDetails, b: IFluidCodeDetails): Promise<number | undefined> {
    // By returning undefined, it is basically saying we don't want to
    // answer the question of what the order is- so that would bypass that loader check.
    const aNameVersion = tryGetNameVersionFromCodeDetails(a);
    if (aNameVersion === undefined) {
      return undefined;
    }
    const bNameVersion = tryGetNameVersionFromCodeDetails(b);
    if (bNameVersion === undefined) {
      return undefined;
    }
    // Different package names cannot be compared.
    if (aNameVersion.name !== bNameVersion.name) {
      return undefined;
    }
    if (aNameVersion.version === bNameVersion.version) {
      return 0;
    }
    // Always treat initialPkgVersion as older.
    if (aNameVersion.version === initialPkgVersion) {
      return -1;
    }
    if (bNameVersion.version === initialPkgVersion) {
      return 1;
    }
    // The core loader in Fluid Framework will not propose code that is
    // is older than the current code based on this comparison.
    // For now we are relying on finite increasing numeric versions.
    return aNameVersion.version - bNameVersion.version;
  }
}
